<template>
  <div
    @mousedown="handleMouseDown"
    class="draggable-wrapper"
    :class="{ active: isActive, dragging: isDragging }"
    :style="{
      left: buttonPosition.x + 'px',
      top: buttonPosition.y + 'px',
      position: 'fixed',
      width: elementSize + 'px',
      height: elementSize + 'px',
    }"
    :title="title"
    :hidden="!isVisible"
  >
    <div @click="handleClick" class="draggable-content" :class="{ dragging: isDragging }">
      <slot>
        <!-- Default button content -->
        <Button variant="primary" size="md" />
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import Button from './ui/Button.vue'
import createLogger from '@/utils/logger'

const logger = createLogger('DraggableButton.vue')

interface Props {
  title?: string
  isActive?: boolean
  isVisible?: boolean
  defaultPosition?: { x: number; y: number }
  storageKey?: string
  elementSize?: number
  margin?: number
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Draggable Element',
  isActive: false,
  isVisible: true,
  defaultPosition: () => ({ x: 40, y: 120 }),
  storageKey: 'draggable_element_position',
  elementSize: 50,
  margin: 20,
})

const emit = defineEmits<{
  (e: 'click'): void
}>()

// Draggable button state
const isDragging = ref(false)
const buttonPosition = ref({ ...props.defaultPosition })
const dragOffset = ref({ x: 0, y: 0 })
let resizeTimeout: number | null = null
let animationFrameId: number | null = null

onMounted(() => {
  loadButtonPosition()
  addDragEventListeners()
  addResizeEventListener()
})

onUnmounted(() => {
  removeDragEventListeners()
  removeResizeEventListener()
  // Clear any pending resize timeout
  if (resizeTimeout) {
    clearTimeout(resizeTimeout)
  }
  // Cancel any pending animation frame
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
  }
})

function handleClick() {
  if (!isDragging.value) {
    emit('click')
  }
}

// Drag functionality
function loadButtonPosition() {
  const savedPosition = localStorage.getItem(props.storageKey)
  if (savedPosition) {
    try {
      const position = JSON.parse(savedPosition)
      buttonPosition.value = position
      // Validate position after loading to ensure it's within current viewport
      validateAndAdjustPosition()
    } catch (e) {
      logger.warn('Failed to parse saved button position:', e)
    }
  }
}

function saveButtonPosition() {
  localStorage.setItem(props.storageKey, JSON.stringify(buttonPosition.value))
}

function addDragEventListeners() {
  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

function removeDragEventListeners() {
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
}

function handleMouseDown(event: MouseEvent) {
  if (props.isActive) return // Don't drag when popup/modal is open

  isDragging.value = true
  const rect = (event.target as HTMLElement).getBoundingClientRect()
  dragOffset.value = {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top,
  }

  // Prevent text selection during drag
  event.preventDefault()
}

function handleMouseMove(event: MouseEvent) {
  if (!isDragging.value) return

  // Cancel any pending animation frame
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
  }

  // Use requestAnimationFrame for smooth updates
  animationFrameId = requestAnimationFrame(() => {
    const newX = event.clientX - dragOffset.value.x
    const newY = event.clientY - dragOffset.value.y

    // Constrain to viewport bounds with margin
    const maxX = window.innerWidth - props.elementSize - props.margin
    const maxY = window.innerHeight - props.elementSize - props.margin

    buttonPosition.value = {
      x: Math.max(props.margin, Math.min(newX, maxX)),
      y: Math.max(props.margin, Math.min(newY, maxY)),
    }

    animationFrameId = null
  })
}

function handleMouseUp() {
  if (isDragging.value) {
    isDragging.value = false
    saveButtonPosition()

    // Cancel any pending animation frame
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId)
      animationFrameId = null
    }
  }
}

// Window resize handling
function addResizeEventListener() {
  window.addEventListener('resize', handleWindowResize)
}

function removeResizeEventListener() {
  window.removeEventListener('resize', handleWindowResize)
}

function handleWindowResize() {
  // Debounce resize events to avoid excessive calls
  if (resizeTimeout) {
    clearTimeout(resizeTimeout)
  }

  resizeTimeout = window.setTimeout(() => {
    validateAndAdjustPosition()
    resizeTimeout = null
  }, 100) // Wait 100ms after resize stops
}

function validateAndAdjustPosition() {
  const maxX = window.innerWidth - props.elementSize - props.margin
  const maxY = window.innerHeight - props.elementSize - props.margin

  // Check if button is outside viewport bounds
  const needsAdjustment =
    buttonPosition.value.x < props.margin ||
    buttonPosition.value.x > maxX ||
    buttonPosition.value.y < props.margin ||
    buttonPosition.value.y > maxY

  if (needsAdjustment) {
    // Adjust position to stay within bounds
    buttonPosition.value = {
      x: Math.max(props.margin, Math.min(buttonPosition.value.x, maxX)),
      y: Math.max(props.margin, Math.min(buttonPosition.value.y, maxY)),
    }

    // Save the adjusted position
    saveButtonPosition()
  }
}
</script>

<style scoped>
.draggable-wrapper {
  cursor: grab;
  user-select: none;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
}

.draggable-wrapper.dragging {
  cursor: grabbing;
}

.draggable-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.draggable-content.dragging {
  transform: scale(1.05);
  transition: none; /* Disable transitions during drag for immediate response */
}

/* Default button styling for when no slot content is provided */
.default-button {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #3b82f6;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition:
    background-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.default-button:hover {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.draggable-wrapper.active .default-button {
  background: #1d4ed8;
}

.draggable-wrapper.dragging .default-button {
  opacity: 0.9;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
}
</style>
